import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FiPhone, FiMail, FiMapPin, FiClock, FiFacebook, FiInstagram, FiLinkedin } from 'react-icons/fi';
import { FaXTwitter, FaWhatsapp } from 'react-icons/fa6';

const Footer = () => {
    const { t } = useTranslation();

    const quickLinks = [
        { name: t('nav.home'), path: '/' },
        { name: t('nav.about'), path: '/about' },
        { name: t('nav.services'), path: '/services' },
        { name: t('nav.blogs'), path: '/blogs' },
        { name: t('nav.careers'), path: '/careers' },
        { name: t('nav.contact'), path: '/contact' },
    ];

    const services = [
        { name: t('common.services.Commercial Cleaning'), path: '/services#commercial' },
        { name: t('common.services.Residential Cleaning'), path: '/services#residential' },
        { name: t('common.services.Industrial Cleaning'), path: '/services#industrial' },
        { name: t('common.services.Rope Access Window Cleaning'), path: '/services#ropeAccess' },
        { name: t('common.services.Floor Care & Marble Polishing'), path: '/services#floorCare' },
        { name: t('common.services.Carpet & Upholstery Cleaning'), path: '/services#carpet' },
        { name: t('common.services.Facility Support Services'), path: '/services#facility' },
        { name: t('common.services.Pest Control & Disinfection'), path: '/services#pest' },
    ];

    const socialLinks = [
        { icon: FiFacebook, href: 'https://www.facebook.com/profile.php?id=61593240141351', label: 'Facebook' },
        { icon: FiInstagram, href: 'https://www.instagram.com/primeforceservices/', label: 'Instagram' },
        { icon: FiLinkedin, href: 'https://www.linkedin.com/company/primeforceservices/', label: 'LinkedIn' },
    ];

    return (
        <footer className="bg-gray-950 text-white overflow-hidden border-t border-gray-800">
            {/* Main Footer */}
            <div className="container-custom py-16">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
                    {/* Company Info */}
                    <div className="space-y-6">
                        <Link to="/" className="inline-block">
                            <img
                                src="/images/logo.png"
                                alt="PrimeForce Services W.L.L."
                                className="h-20 w-auto bg-white p-2 rounded-xl shadow-md"
                            />
                        </Link>
                        <div className="text-gray-300 leading-relaxed space-y-2 text-sm">
                            <p className="font-bold text-white text-base">PrimeForce Services W.L.L.</p>
                            <p>{t('footerSection.description')}</p>
                        </div>
                        <div className="flex gap-3 pt-2">
                            {socialLinks.map((social) => (
                                <a
                                    key={social.label}
                                    href={social.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={social.label}
                                    className="w-10 h-10 bg-gray-800 hover:bg-accent-500 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 text-gray-300 hover:text-white"
                                >
                                    <social.icon size={18} />
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h3 className="text-lg font-semibold mb-6 relative inline-block">
                            {t('footerSection.quickLinks')}
                            <span className="absolute -bottom-2 start-0 w-12 h-1 bg-accent-500 rounded-full"></span>
                        </h3>
                        <ul className="space-y-3">
                            {quickLinks.map((link) => (
                                <li key={link.path}>
                                    <Link
                                        to={link.path}
                                        className="text-gray-400 hover:text-accent-400 transition-colors duration-300 flex items-center gap-2 group text-sm"
                                    >
                                        <span className="w-0 group-hover:w-2 h-0.5 bg-accent-500 transition-all duration-300"></span>
                                        {link.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Services */}
                    <div>
                        <h3 className="text-lg font-semibold mb-6 relative inline-block">
                            {t('footerSection.services')}
                            <span className="absolute -bottom-2 start-0 w-12 h-1 bg-accent-500 rounded-full"></span>
                        </h3>
                        <ul className="space-y-2.5">
                            {services.map((service) => (
                                <li key={service.name}>
                                    <Link
                                        to={service.path}
                                        className="text-gray-400 hover:text-accent-400 transition-colors duration-300 flex items-center gap-2 group text-sm"
                                    >
                                        <span className="w-0 group-hover:w-2 h-0.5 bg-accent-500 transition-all duration-300"></span>
                                        {service.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact Info */}
                    <div>
                        <h3 className="text-lg font-semibold mb-6 relative inline-block">
                            {t('footerSection.contact')}
                            <span className="absolute -bottom-2 start-0 w-12 h-1 bg-accent-500 rounded-full"></span>
                        </h3>
                        <ul className="space-y-3.5 text-sm">
                            <li>
                                <a href="tel:+97317701616" className="flex items-start gap-3 text-gray-400 hover:text-accent-400 transition-colors">
                                    <FiPhone className="text-accent-500 mt-0.5 flex-shrink-0" size={18} />
                                    <span className="font-medium">+973 1770 1616</span>
                                </a>
                            </li>
                            <li>
                                <a href="https://wa.me/97366752929" target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 text-gray-400 hover:text-green-400 transition-colors">
                                    <FaWhatsapp className="text-green-500 mt-0.5 flex-shrink-0" size={18} />
                                    <span className="font-medium">+973 6675 2929</span>
                                </a>
                            </li>
                            <li>
                                <a href="mailto:info@primeforceservices.com" className="flex items-start gap-3 text-gray-400 hover:text-accent-400 transition-colors break-all">
                                    <FiMail className="text-accent-500 mt-0.5 flex-shrink-0" size={18} />
                                    <span className="break-all font-medium">info@primeforceservices.com</span>
                                </a>
                            </li>
                            <li className="flex items-start gap-3 text-gray-400">
                                <FiClock className="text-accent-500 mt-0.5 flex-shrink-0" size={18} />
                                <span className="leading-snug">{t('contactPage.info.hoursValue')}</span>
                            </li>
                            <li>
                                <a
                                    href="https://www.google.com/maps/place/26%C2%B013'52.2%22N+50%C2%B035'10.8%22E/@26.2311726,50.586338,17z"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-start gap-3 text-gray-400 hover:text-accent-400 transition-colors"
                                >
                                    <FiMapPin className="text-accent-500 mt-0.5 flex-shrink-0" size={18} />
                                    <span className="leading-snug">{t('contactPage.info.locationValue')}</span>
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Bottom Footer */}
            <div className="border-t border-gray-900 bg-gray-950">
                <div className="container-custom py-6 flex flex-col md:flex-row justify-between items-center gap-4">
                    <p className="text-gray-400 text-xs md:text-sm text-center md:text-left">
                        © 2026 PrimeForce Services W.L.L. {t('footer.rights')}<br />
                        <span className="text-gray-500 text-xs">CR No: 182919-1 | VAT: 220025260900002</span>
                    </p>
                    <div className="flex gap-6 text-xs text-gray-400">
                        <Link to="/privacy-policy" className="hover:text-accent-400 transition-colors">{t('footerSection.privacy')}</Link>
                        <Link to="/terms-of-service" className="hover:text-accent-400 transition-colors">{t('footerSection.terms')}</Link>
                    </div>
                </div>
                <div className="container-custom pb-6">
                    <p className="text-gray-500 text-xs text-center">
                     Designed and Developed by  {' '}
                        <a
                            href="https://www.vikrin.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-accent-400 hover:text-white font-semibold transition-all duration-300 hover:underline"
                        >
                            Vikrin Pvt Ltd 
                        </a>
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
